// src/frontend/src/pages/RefundPage.js
import { Box, Container, Typography, Paper, Alert } from '@mui/material';

const RefundPage = () => {
    return (
        <Container maxWidth="md" sx={{ py: 6 }}>
            <Paper elevation={0} sx={{ p: { xs: 3, md: 5 } }}>
                <Typography variant="h3" component="h1" fontWeight="bold" gutterBottom>
                    Refund Policy
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
                    Last updated: September 19, 2026
                </Typography>

                <Alert severity="info" sx={{ mb: 4 }}>
                    We want you to be completely satisfied with StockMaid Premium. If you're not happy with our service, we're here to help.
                </Alert>

                <Box sx={{ '& h2': { mt: 4, mb: 2 }, '& p': { mb: 2, lineHeight: 1.8 } }}>
                    <Typography variant="h5" component="h2" fontWeight="600">
                        1. Free Trial
                    </Typography>
                    <Typography variant="body1">
                        We offer a 3-day free trial for new users to experience StockMaid Premium before committing to a paid subscription. A payment method is required to start the trial. During the trial period:
                    </Typography>
                    <Box component="ul" sx={{ pl: 3, mb: 2 }}>
                        <li><Typography variant="body1">You have full access to all premium features</Typography></li>
                        <li><Typography variant="body1">You can cancel at any time before the trial ends without being charged</Typography></li>
                        <li><Typography variant="body1">If you don't cancel, your subscription will automatically convert to a paid subscription at $9.99 USD per month when the 3 days end</Typography></li>
                    </Box>
                    <Typography variant="body1">
                        Free trials are limited to one per person or household. Creating multiple accounts to obtain additional free trials is prohibited.
                    </Typography>

                    <Typography variant="h5" component="h2" fontWeight="600">
                        2. Subscription Refunds
                    </Typography>
                    <Typography variant="h6" component="h3" fontWeight="500" sx={{ mt: 2 }}>
                        2.1 First-Time Subscribers
                    </Typography>
                    <Typography variant="body1">
                        If you are a first-time subscriber and are not satisfied with StockMaid Premium, you may request a full refund within <strong>7 days</strong> of your initial purchase. This applies to your first subscription payment only.
                    </Typography>

                    <Typography variant="h6" component="h3" fontWeight="500" sx={{ mt: 2 }}>
                        2.2 Recurring Payments
                    </Typography>
                    <Typography variant="body1">
                        For recurring monthly payments after your first month:
                    </Typography>
                    <Box component="ul" sx={{ pl: 3, mb: 2 }}>
                        <li><Typography variant="body1">Refunds are generally not provided for partial months</Typography></li>
                        <li><Typography variant="body1">You may cancel at any time and retain access until the end of your billing period</Typography></li>
                        <li><Typography variant="body1">We may offer prorated refunds at our discretion for exceptional circumstances</Typography></li>
                    </Box>

                    <Typography variant="h6" component="h3" fontWeight="500" sx={{ mt: 2 }}>
                        2.3 Accidental Charges
                    </Typography>
                    <Typography variant="body1">
                        If you were charged accidentally (e.g., after cancelling or during a technical error), please contact us immediately. We will review your case and issue a full refund if the charge was made in error.
                    </Typography>

                    <Typography variant="h5" component="h2" fontWeight="600">
                        3. How to Request a Refund
                    </Typography>
                    <Typography variant="body1">
                        To request a refund, please:
                    </Typography>
                    <Box component="ol" sx={{ pl: 3, mb: 2 }}>
                        <li><Typography variant="body1">Email us at <strong>support@stockmaid.com</strong></Typography></li>
                        <li><Typography variant="body1">Include the email address associated with your account</Typography></li>
                        <li><Typography variant="body1">Briefly describe the reason for your refund request</Typography></li>
                    </Box>
                    <Typography variant="body1">
                        We aim to respond to all refund requests within <strong>2 business days</strong>. Approved refunds will be processed within <strong>5-10 business days</strong> and credited to your original payment method.
                    </Typography>

                    <Typography variant="h5" component="h2" fontWeight="600">
                        4. When Refunds May Not Be Provided
                    </Typography>
                    <Typography variant="body1">
                        Refunds may be denied in the following situations:
                    </Typography>
                    <Box component="ul" sx={{ pl: 3, mb: 2 }}>
                        <li><Typography variant="body1">Requests made more than 7 days after the first purchase</Typography></li>
                        <li><Typography variant="body1">Multiple refund requests from the same user</Typography></li>
                        <li><Typography variant="body1">Violations of our Terms of Service</Typography></li>
                        <li><Typography variant="body1">Abuse of the refund policy (e.g., subscribing repeatedly for free trials)</Typography></li>
                        <li><Typography variant="body1">Failure to cancel before a renewal date (please cancel in advance)</Typography></li>
                    </Box>

                    <Typography variant="h5" component="h2" fontWeight="600">
                        5. Cancellation vs. Refund
                    </Typography>
                    <Typography variant="body1">
                        Please note the difference between cancellation and refund:
                    </Typography>
                    <Box component="ul" sx={{ pl: 3, mb: 2 }}>
                        <li><Typography variant="body1"><strong>Cancellation:</strong> Stops future charges. You keep premium access until the end of your current billing period.</Typography></li>
                        <li><Typography variant="body1"><strong>Refund:</strong> Returns money for a charge. If approved, your premium access ends immediately.</Typography></li>
                    </Box>
                    <Typography variant="body1">
                        If you simply want to stop using StockMaid Premium in the future, cancellation is the appropriate option. You can cancel anytime from your Profile page.
                    </Typography>

                    <Typography variant="h5" component="h2" fontWeight="600">
                        6. Service Issues
                    </Typography>
                    <Typography variant="body1">
                        If you experienced technical issues that prevented you from using the Service during your subscription period, please contact us. We may offer:
                    </Typography>
                    <Box component="ul" sx={{ pl: 3, mb: 2 }}>
                        <li><Typography variant="body1">Service credits for extended downtime</Typography></li>
                        <li><Typography variant="body1">Partial refunds for significant service disruptions</Typography></li>
                        <li><Typography variant="body1">Extension of your subscription period</Typography></li>
                    </Box>
                    <Typography variant="body1">
                        Please note that we cannot provide refunds for missed product purchases, as product availability is determined by third-party retailers and is outside our control.
                    </Typography>

                    <Typography variant="h5" component="h2" fontWeight="600">
                        7. Chargebacks
                    </Typography>
                    <Typography variant="body1">
                        We encourage you to contact us directly before initiating a chargeback with your bank or credit card company. Chargebacks result in additional fees and may lead to account suspension. We are committed to resolving any issues fairly and promptly.
                    </Typography>

                    <Typography variant="h5" component="h2" fontWeight="600">
                        8. Changes to This Policy
                    </Typography>
                    <Typography variant="body1">
                        We may update this Refund Policy from time to time. Changes will be posted on this page with an updated revision date. The policy in effect at the time of your purchase will apply to that transaction.
                    </Typography>

                    <Typography variant="h5" component="h2" fontWeight="600">
                        9. Contact Us
                    </Typography>
                    <Typography variant="body1">
                        If you have questions about our Refund Policy or need assistance:
                    </Typography>
                    <Box component="ul" sx={{ pl: 3, mb: 2 }}>
                        <li><Typography variant="body1">Email: support@stockmaid.com</Typography></li>
                        <li><Typography variant="body1">Discord: discord.gg/W78pdPCdbC</Typography></li>
                    </Box>
                    <Typography variant="body1">
                        We're here to help and want to ensure you have a positive experience with StockMaid.
                    </Typography>
                </Box>
            </Paper>
        </Container>
    );
};

export default RefundPage;